<main class="h-full flex flex-col">
  <header class="okto-header">
    <div class="h-full px-4 lg:px-6 flex flex-row items-center justify-between w-full">
      <!-- Left Zone: Toggle, Logo & Org Selector -->
      @if (!smallLayout || !mobileSearchActive) {
      <div class="flex items-center gap-2">
        @if (menuDisplayed()) {
        <button mat-icon-button class="menu-toggle-btn" (click)="menuOpened = !menuOpened" aria-label="Toggle menu">
          <mat-icon [class.rotate-180]="menuOpened" class="transition-transform duration-300">
            {{ menuOpened ? 'menu_open' : 'menu' }}
          </mat-icon>
        </button>
        }

        @if (smallLayout) {
        <div class="cursor-pointer flex items-center logo-mobile-wrapper" routerLink="/">
          <img src="./assets/images/colored-logo.svg" class="logo-mobile-img" alt="Okto logo">
        </div>
        }

        @if (authService.isLoggedIn && organizationName) {
        <div class="relative">
          @if (!smallLayout) {
          <button [matMenuTriggerFor]="organizationMenu" class="logo-org-capsule-btn">
            <div class="logo-section">
              <img src="./assets/images/colored-logo.svg" class="logo-img" alt="Okto logo">
            </div>
            <div class="capsule-divider"></div>
            <div class="org-section">
              <mat-icon class="org-icon">business</mat-icon>
              <span class="org-name-text">{{ organizationName }}</span>
              <mat-icon class="arrow-icon">keyboard_arrow_down</mat-icon>
            </div>
          </button>
          } @else {
          <button mat-icon-button [matMenuTriggerFor]="organizationMenu" class="org-selector-btn-mobile" aria-label="Select organization">
            <mat-icon class="text-primary">business</mat-icon>
          </button>
          }

          <mat-menu #organizationMenu="matMenu" class="premium-menu min-w-64" xPosition="after">

            <button mat-menu-item routerLink="/" class="menu-item-with-icon">
              <mat-icon class="text-primary">storefront</mat-icon>
              <span>{{ 'home' | translate }}</span>
            </button>

            @if (userOrganizations.length > 0) {
            <mat-divider class="my-1"></mat-divider>
            <div class="max-h-60 overflow-y-auto">
              @for (organization of userOrganizations; track organization) {
              @if (organizationNames[organization.id] !== organizationName) {
              <button mat-menu-item (click)="goToOrganization(organization)" class="menu-item-with-icon">
                <mat-icon class="opacity-60">business_center</mat-icon>
                <span>{{ organizationNames[organization.id] }}</span>
              </button>
              }
              }
            </div>
            }
          </mat-menu>
        </div>
        } @else if (!smallLayout) {
        <div class="cursor-pointer logo-container-wrapper" routerLink="/">
          <div class="logo-capsule">
            <img src="./assets/images/colored-logo.svg" class="logo-img" alt="Okto logo">
          </div>
        </div>
        }
      </div>
      }

      <!-- Center Zone: Spotlight Search Bar -->
      @if (authService.hasRole([RolesEnum.WORKSHOP_WRITE, RolesEnum.ORDER_WRITE, RolesEnum.SALES])) {
        @if (!smallLayout || mobileSearchActive) {
        <div class="flex-1 flex justify-center mx-4" [class.max-w-2xl]="!smallLayout" [class.w-full]="smallLayout">
          <div class="search-container relative w-full" [class.focused]="searchFocused">
            <div class="search-box">
              @if (smallLayout) {
              <button mat-icon-button class="close-mobile-search-btn" (click)="closeMobileSearch()" aria-label="Close search">
                <mat-icon>arrow_back</mat-icon>
              </button>
              } @else {
              <mat-icon class="search-icon">search</mat-icon>
              }
              <input [(ngModel)]="searchedValue" (focusin)="onSearchFocus()" (focusout)="onSearchBlur()"
                (ngModelChange)="find()" type="text" placeholder="{{ 'synchroniseShipments' | translate }}"
                class="search-input" />
              @if (searchedValue) {
              <button mat-icon-button class="clear-search-btn" (click)="clearSearch($event)" aria-label="Clear search">
                <mat-icon>close</mat-icon>
              </button>
              } @else if (!searchFocused && !smallLayout) {
              <kbd class="search-shortcut">/</kbd>
              }
            </div>

            @if (searchOpened) {
            <div class="search-backdrop-overlay" (click)="closeSearch()"></div>
            <mat-card class="search-results-card mat-elevation-z8">
              @if (authService.hasRole([RolesEnum.ORDER_WRITE, RolesEnum.SALES])) {
              <div class="import-section">
                <h3 class="import-message">{{ 'importOrderQuotationMessage' | translate }}</h3>
                <button mat-flat-button color="primary" class="import-btn" (click)="import()" [disabled]="importProgress">
                  @if (importProgress) {
                  <mat-spinner diameter="18" class="mr-2"></mat-spinner>
                  } @else {
                  <mat-icon class="mr-2">cloud_upload</mat-icon>
                  }
                  {{ 'imports.start.button' | translate }}
                </button>
              </div>
              }
              @if (importProgress) {
              <mat-progress-bar mode="indeterminate" class="my-3" color="primary"></mat-progress-bar>
              }

              <div class="results-content scrollbar-thin max-h-[60vh] overflow-y-auto">
                @if (orders.length) {
                <div class="results-group">
                  <div class="group-header">
                    <mat-icon>shopping_bag</mat-icon>
                    <span>{{ 'ordersTracking' | translate }}</span>
                  </div>
                  @for (order of orders; track order) {
                  <div class="result-item-wrapper">
                    <app-order-navigation [order]="order" [withBackButton]="false"
                      [selectable]="true"></app-order-navigation>
                  </div>
                  }
                </div>
                }

                @if (quotations.length) {
                <div class="results-group mt-4">
                  <div class="group-header">
                    <mat-icon>request_quote</mat-icon>
                    <span>{{ 'quotation' | translate }}</span>
                  </div>
                  @for (quotation of quotations; track quotation) {
                  <div class="result-item-wrapper">
                    <app-quotation-menu [quotation]="quotation" [withBackButton]="false"
                      [selectable]="true"></app-quotation-menu>
                  </div>
                  }
                </div>
                }

                @if (!orders.length && !quotations.length && !importProgress) {
                <div class="no-results py-6 text-center opacity-60 flex flex-col items-center">
                  <mat-icon class="text-3xl mb-1">search_off</mat-icon>
                  <p class="text-sm">{{ 'search.noResultsFound' | translate }}</p>
                </div>
                }
              </div>
            </mat-card>
            }
          </div>
        </div>
        }
      }

      <!-- Right Zone: Theme Switcher & Profile -->
      @if (!smallLayout || !mobileSearchActive) {
      <div class="flex items-center gap-3">
        @if (smallLayout && authService.hasRole([RolesEnum.WORKSHOP_WRITE, RolesEnum.ORDER_WRITE, RolesEnum.SALES])) {
        <button mat-icon-button class="mobile-search-trigger-btn" (click)="openMobileSearch()" aria-label="Open search">
          <mat-icon>search</mat-icon>
        </button>
        }

        <app-menu (themeChange)="themeChangeHandler($event)" class="theme-switcher-wrapper"></app-menu>

        @if (authService.isLoggedIn) {
        <div class="profile-trigger" [matMenuTriggerFor]="profileMenu">
          @if (!smallLayout && authService.currentUser.displayName) {
          <div class="profile-info-text text-right mr-3 hidden md:flex flex-col">
            <span class="user-display-name text-xs font-semibold tracking-tight text-foreground leading-tight">
              {{ authService.currentUser.displayName }}
            </span>
            <span
              class="user-role-text text-[10px] font-medium tracking-wide text-primary uppercase leading-none opacity-85 mt-0.5">
              {{ 'active' | translate }}
            </span>
          </div>
          }
          <div class="avatar-container">
            @if (authService.currentUser.photoURL) {
            <img src="{{authService.currentUser.photoURL}}" class="avatar-image" alt="user profile image">
            } @else if (authService.currentUser.displayName) {
            <div class="avatar-initial">
              {{ authService.currentUser.displayName.substring(0, 1).toUpperCase() }}
            </div>
            } @else {
            <div class="avatar-initial">
              <mat-icon>person</mat-icon>
            </div>
            }
            <div class="active-dot"></div>
          </div>
        </div>

        <mat-menu #profileMenu="matMenu" class="premium-menu profile-menu" xPosition="before">
          <div class="profile-header-card px-4 py-3 border-b border-divider mb-1">
            <div class="font-bold text-sm leading-tight text-foreground">
              {{ authService.currentUser.displayName || ('user' | translate) }}
            </div>
            <div class="text-xs text-muted-foreground truncate max-w-56 mt-0.5">
              {{ authService.currentUser.email }}
            </div>
          </div>

          <button mat-menu-item (click)="openProfileDialog()" class="menu-item-with-icon">
            <mat-icon class="text-primary">manage_accounts</mat-icon>
            <span>{{ 'account.edit.profile' | translate }}</span>
          </button>

          <button mat-menu-item (click)="authService.signOut()" class="menu-item-with-icon">
            <mat-icon class="opacity-60">exit_to_app</mat-icon>
            <span>{{ 'DASHBOARD.LOGOUT' | translate }}</span>
          </button>
        </mat-menu>
        }
      </div>
      }
    </div>
  </header>
  <mat-sidenav-container class="flex-1 min-h-0">
    @if (menuDisplayed()) {
    <mat-sidenav [mode]="smallLayout ? 'over' : 'side'" class="okto-sidenav" [opened]="smallLayout ? menuOpened : true"
      [ngClass]="{ 'closed-sidenav' : !menuOpened && !smallLayout}" (closed)="menuOpened = false">
      <!--Dashboard-->
      <mat-nav-list>
        <mat-list-item (click)="openCommonPath('dashboard')" [class.active-nav-item]="isRouteActive('dashboard')">
          <mat-icon matListItemIcon>dashboard</mat-icon>
          @if (menuOpened) {
          <div matListItemTitle>{{ 'dashboard' | translate }}</div>
          }
        </mat-list-item>
        @if (customers.length || isSupplierOf ||
        authService.hasRole([RolesEnum.ORDER_WRITE, RolesEnum.SALES, RolesEnum.EXTERNAL_PRODUCTION_WRITE])) {
        <mat-list-item (click)="openCommonPath('orders')" [class.active-nav-item]="isRouteActive('orders')">
          <mat-icon matListItemIcon>shopping_bag</mat-icon>
          @if (menuOpened) {
          <div matListItemTitle>{{ 'myOrders' | translate }}</div>
          }
        </mat-list-item>
        }
        @if (organizationService.currentOrganization.plugins.includes(PluginEnum.MULTIPRESS)) {
        @if ((customers.length && queryParams?.accessMode === 'customer') || authService.hasRole(RolesEnum.SALES)) {
        <mat-list-item (click)="openCommonPath('quotations')" [class.active-nav-item]="isRouteActive('quotations')">
          <mat-icon matListItemIcon>request_quote</mat-icon>
          @if (menuOpened) {
          <div matListItemTitle>{{ 'myQuotations' | translate }}</div>
          }
        </mat-list-item>
        }
        }
      </mat-nav-list>
      <!--Commerce-->
      @if (authService.hasRole([RolesEnum.CUSTOMER_WRITE, RolesEnum.COMMERCE_WRITE, RolesEnum.PRODUCT_WRITE])) {
      @if (menuOpened) {
      <div class="menu-section-header-container">
        <span class="menu-section-header">{{ 'business.adminMenu.title'| translate }}</span>
      </div>
      }
      <mat-nav-list>
        @if (authService.hasRole(RolesEnum.COMMERCE_WRITE)) {
        <a mat-list-item [routerLink]="[organizationService.currentOrganization.oid, 'shops']"
          routerLinkActive="active-nav-item" [routerLinkActiveOptions]="{ exact: false }" (click)="goToAction()">
          <mat-icon matListItemIcon>store</mat-icon>
          @if (menuOpened) {
          <span>{{ 'shops.menu.title' | translate }}</span>
          }
        </a>
        }
        @if (authService.hasRole(RolesEnum.PRODUCT_WRITE)) {
        <a mat-list-item [routerLink]="[organizationService.currentOrganization.oid, 'products']"
          routerLinkActive="active-nav-item" [routerLinkActiveOptions]="{ exact: false }" (click)="goToAction()">
          <mat-icon matListItemIcon>category</mat-icon>
          @if (menuOpened) {
          <span>{{ 'products.menu.title' | translate }}</span>
          }
        </a>
        }
        @if (organizationService.currentOrganization.plugins.includes(PluginEnum.MULTIPRESS) &&
        authService.hasRole(RolesEnum.CUSTOMER_WRITE)) {
        <mat-list-item [routerLink]="[organizationService.currentOrganization.oid, 'customers']"
          routerLinkActive="active-nav-item" [routerLinkActiveOptions]="{ exact: false }" class="admin-menu-list"
          (click)="goToAction()">
          <mat-icon matListItemIcon>groups</mat-icon>
          @if (menuOpened) {
          <span>{{ 'business.menu.title' | translate }}</span>
          }
        </mat-list-item>
        }
      </mat-nav-list>
      }
      <!--Pre-press-->
      @if (authService.hasRole([RolesEnum.SCRIPT_WRITE, RolesEnum.PREPRESS_WRITE]) &&
      organizationService.currentOrganization.plugins.includes(PluginEnum.MULTIPRESS)) {
      <div>
        @if (menuOpened) {
        <div class="menu-section-header-container">
          <span class="menu-section-header">{{ 'ADMIN.PRODUCTION' | translate }}</span>
          @if (authService.hasRole(RolesEnum.PREPRESS_WRITE)) {
          <button class="section-settings-btn" mat-icon-button
            [routerLink]="[organizationService.currentOrganization.oid, 'files-settings']">
            <mat-icon>settings</mat-icon>
          </button>
          }
        </div>
        }
        <mat-nav-list>
          @if (authService.hasRole(RolesEnum.SCRIPT_WRITE)) {
          <mat-list-item [routerLink]="[organizationService.currentOrganization.oid, 'scripts']"
            routerLinkActive="active-nav-item" [routerLinkActiveOptions]="{ exact: false }" class="admin-menu-list"
            (click)="goToAction()">
            <mat-icon matListItemIcon>integration_instructions</mat-icon>
            @if (menuOpened) {
            <span>{{ 'scripts' | translate }}</span>
            }
          </mat-list-item>
          }
        </mat-nav-list>
      </div>
      }
      @if (organizationService.currentOrganization.plugins.includes(PluginEnum.MULTIPRESS)) {
      <!--Production-->
      @if (authService.hasRole([RolesEnum.PLANNING_WRITE, RolesEnum.WORKSHOP_WRITE, RolesEnum.STOCK_WRITE])) {
      <div>
        @if (menuOpened) {
        <div class="menu-section-header-container">
          <span class="menu-section-header">{{ 'settings.okto.production.title' | translate }}</span>
        </div>
        }
        <mat-nav-list>
          @if (authService.hasRole(RolesEnum.PLANNING_WRITE)) {
          <mat-list-item [routerLink]="[organizationService.currentOrganization.oid, 'planning-dashboard']"
            routerLinkActive="active-nav-item" [routerLinkActiveOptions]="{ exact: false }" (click)="goToAction()">
            <mat-icon matListItemIcon>bar_chart</mat-icon>
            @if (menuOpened) {
            <div matListItemTitle>{{ 'planning.dashboard.menu' | translate }}</div>
            }
          </mat-list-item>
          }
          @if (authService.hasRole([RolesEnum.PLANNING_WRITE, RolesEnum.WORKSHOP_WRITE])) {
          <mat-list-item [routerLink]="[organizationService.currentOrganization.oid, 'planning']"
            routerLinkActive="active-nav-item" [routerLinkActiveOptions]="{ exact: false }" (click)="goToAction()">
            <mat-icon matListItemIcon>date_range</mat-icon>
            @if (menuOpened) {
            <div matListItemTitle>{{ 'planning' | translate }}</div>
            }
          </mat-list-item>
          }
          @if (authService.hasRole(RolesEnum.PLANNING_WRITE)) {
          <mat-list-item [routerLink]="[organizationService.currentOrganization.oid, 'anomalies']"
            routerLinkActive="active-nav-item" [routerLinkActiveOptions]="{ exact: false }" (click)="goToAction()">
            <mat-icon matListItemIcon>report_problem</mat-icon>
            @if (menuOpened) {
            <div matListItemTitle>{{ 'planning.anomaly.menu' | translate }}</div>
            }
          </mat-list-item>
          <mat-list-item [routerLink]="[organizationService.currentOrganization.oid, 'machines']"
            routerLinkActive="active-nav-item" [routerLinkActiveOptions]="{ exact: false }" class="admin-menu-list"
            (click)="goToAction()">
            <mat-icon matListItemIcon>precision_manufacturing</mat-icon>
            @if (menuOpened) {
            <span>{{ 'machines' | translate }}</span>
            }
          </mat-list-item>
          <mat-list-item [routerLink]="[organizationService.currentOrganization.oid, 'manual-operations']"
            routerLinkActive="active-nav-item" [routerLinkActiveOptions]="{ exact: false }" class="admin-menu-list"
            (click)="goToAction()">
            <mat-icon matListItemIcon>pan_tool</mat-icon>
            @if (menuOpened) {
            <span>{{ 'manualOperations.menu' | translate }}</span>
            }
          </mat-list-item>
          }
          @if (authService.hasRole(RolesEnum.STOCK_WRITE)) {
          <mat-list-item [routerLink]="[organizationService.currentOrganization.oid, 'stock']"
            routerLinkActive="active-nav-item" [routerLinkActiveOptions]="{ exact: false }" class="admin-menu-list"
            (click)="goToAction()">
            <mat-icon matListItemIcon>inventory_2</mat-icon>
            @if (menuOpened) {
            <span>{{ 'stock.menu.title' | translate }}</span>
            }
          </mat-list-item>
          }
        </mat-nav-list>
      </div>
      }
      <!--Installation-->
      @if (authService.hasRole([RolesEnum.INSTALLATION_ADMIN, RolesEnum.INSTALLER])) {
      <div>
        @if (menuOpened) {
        <div class="menu-section-header-container">
          <span class="menu-section-header">{{ 'installation.adminMenu.title' | translate }}</span>
        </div>
        }
        <mat-nav-list>
          <mat-list-item [routerLink]="[organizationService.currentOrganization.oid, 'installation', 'agenda']"
            routerLinkActive="active-nav-item" [routerLinkActiveOptions]="{ exact: false }" (click)="goToAction()">
            <mat-icon matListItemIcon>calendar_month</mat-icon>
            @if (menuOpened) {
            <div matListItemTitle>{{ 'installation.agenda.title' | translate }}</div>
            }
          </mat-list-item>
          @if (authService.hasRole(RolesEnum.INSTALLATION_ADMIN)) {
          <mat-list-item [routerLink]="[organizationService.currentOrganization.oid, 'installation', 'resources']"
            routerLinkActive="active-nav-item" [routerLinkActiveOptions]="{ exact: false }" class="admin-menu-list"
            (click)="goToAction()">
            <mat-icon matListItemIcon>home_repair_service</mat-icon>
            @if (menuOpened) {
            <span>{{ 'installation.resources.title' | translate }}</span>
            }
          </mat-list-item>
          }
        </mat-nav-list>
      </div>
      }
      <!--Transport-->
      @if (authService.hasRole([RolesEnum.SHIPMENT_READ, RolesEnum.WORKSHOP_WRITE, RolesEnum.SHIPMENT_WRITE])) {
      <div>
        @if (menuOpened) {
        <div class="menu-section-header-container">
          <span class="menu-section-header">{{ 'shipment.adminMenu.title'| translate }}</span>
          @if (authService.hasRole([RolesEnum.SHIPMENT_WRITE])) {
          <button class="section-settings-btn" mat-icon-button
            [routerLink]="[organizationService.currentOrganization.oid, 'transport-settings']">
            <mat-icon>settings</mat-icon>
          </button>
          }
        </div>
        }
        <mat-nav-list>
          @if (authService.hasRole(RolesEnum.SHIPMENT_READ) &&
          organizationService.currentOrganization.plugins.includes(PluginEnum.MULTIPRESS)) {
          <mat-list-item [routerLink]="[organizationService.currentOrganization.oid, 'shipments']"
            routerLinkActive="active-nav-item" [routerLinkActiveOptions]="{ exact: false }" class="admin-menu-list"
            (click)="goToAction()">
            <mat-icon matListItemIcon>local_shipping</mat-icon>
            @if (menuOpened) {
            <span>{{ 'shipment.menu.title' | translate }}</span>
            }
          </mat-list-item>
          }
          @if (authService.hasRole(RolesEnum.SHIPMENT_READ)) {
          <mat-list-item [routerLink]="[organizationService.currentOrganization.oid, 'closing-day']"
            routerLinkActive="active-nav-item" [routerLinkActiveOptions]="{ exact: false }" class="admin-menu-list"
            (click)="goToAction()">
            <mat-icon matListItemIcon>event_available</mat-icon>
            @if (menuOpened) {
            <span>{{ 'closingDay' | translate }}</span>
            }
          </mat-list-item>
          }
          @if (authService.hasRole(RolesEnum.WORKSHOP_WRITE) &&
          organizationService.currentOrganization.plugins.includes(PluginEnum.MULTIPRESS)) {
          <mat-list-item [routerLink]="[organizationService.currentOrganization.oid, 'workshop']"
            routerLinkActive="active-nav-item" [routerLinkActiveOptions]="{ exact: false }" class="admin-menu-list"
            (click)="goToAction()">
            <mat-icon matListItemIcon>add_location</mat-icon>
            @if (menuOpened) {
            <span>{{ 'shipment.workshopMenu.title' | translate }}</span>
            }
          </mat-list-item>
          }
          @if (authService.hasRole(RolesEnum.WORKSHOP_WRITE) &&
          organizationService.currentOrganization.plugins.includes(PluginEnum.MULTIPRESS)) {
          <mat-list-item [routerLink]="[organizationService.currentOrganization.oid, 'pallet']"
            routerLinkActive="active-nav-item" [routerLinkActiveOptions]="{ exact: false }" class="admin-menu-list"
            (click)="goToAction()">
            <mat-icon matListItemIcon>clear_all</mat-icon>
            @if (menuOpened) {
            <span>{{ 'shipment.palletMenu.title' | translate }}</span>
            }
          </mat-list-item>
          }
          @if (authService.hasRole(RolesEnum.SHIPMENT_READ)) {
          <mat-list-item [routerLink]="[organizationService.currentOrganization.oid, 'transport-calculator']"
            routerLinkActive="active-nav-item" [routerLinkActiveOptions]="{ exact: false }" class="admin-menu-list"
            (click)="goToAction()">
            <mat-icon matListItemIcon>calculate</mat-icon>
            @if (menuOpened) {
            <span>{{ 'shipment.calc.title' | translate }}</span>
            }
          </mat-list-item>
          }
          @if (authService.hasRole(RolesEnum.SHIPMENT_WRITE)) {
          <mat-list-item [routerLink]="[organizationService.currentOrganization.oid, 'transport-prices']"
            routerLinkActive="active-nav-item" [routerLinkActiveOptions]="{ exact: false }" class="admin-menu-list"
            (click)="goToAction()">
            <mat-icon matListItemIcon>grid_on</mat-icon>
            @if (menuOpened) {
            <span>{{ 'settings.transport.pricing' | translate }}</span>
            }
          </mat-list-item>
          }
        </mat-nav-list>
      </div>
      }
      }
      <!--Settings-->
      @if (authService.hasRole([RolesEnum.USER_WRITE])) {
      <div>
        @if (menuOpened) {
        <div class="menu-section-header-container">
          <span class="menu-section-header">{{ 'settings.main.title'| translate }}</span>
        </div>
        }
        <mat-nav-list>
          @if (authService.hasRole(RolesEnum.USER_WRITE)) {
          <mat-list-item [routerLink]="[organizationService.currentOrganization.oid, 'users']"
            routerLinkActive="active-nav-item" [routerLinkActiveOptions]="{ exact: false }" class="admin-menu-list"
            (click)="goToAction()">
            <mat-icon matListItemIcon>admin_panel_settings</mat-icon>
            @if (menuOpened) {
            <span>{{ 'ADMIN.ADMIN_USERS' | translate }}</span>
            }
          </mat-list-item>
          }
        </mat-nav-list>
      </div>
      }
      @if (organizationService.currentOrganization.plugins.includes(PluginEnum.MULTIPRESS)) {
      <!--Plugins-->
      @if (organizationService.currentOrganization.plugins.length &&
      authService.hasRole([RolesEnum.IMPORT_WRITE, RolesEnum.MULTIPRESS_WRITE])) {
      <div>
        @if (menuOpened) {
        <div class="menu-section-header-container">
          <span class="menu-section-header">{{ 'plugins' | translate }}</span>
        </div>
        }
        <mat-nav-list>
          @if (authService.hasRole(RolesEnum.MULTIPRESS_WRITE) &&
          organizationService.currentOrganization.plugins.includes(PluginEnum.MULTIPRESS)) {
          <mat-list-item [routerLink]="[organizationService.currentOrganization.oid, 'multipress']"
            routerLinkActive="active-nav-item" [routerLinkActiveOptions]="{ exact: false }" class="admin-menu-list"
            (click)="goToAction()">
            <mat-icon matListItemIcon>mp</mat-icon>
            @if (menuOpened) {
            <span>{{ 'multipress' | translate }}</span>
            }
          </mat-list-item>
          }
          @if (authService.hasRole(RolesEnum.IMPORT_WRITE) &&
          organizationService.currentOrganization.plugins.includes(PluginEnum.PRINTOCLOCK)) {
          <mat-list-item [routerLink]="[organizationService.currentOrganization.oid, 'printoclock']"
            routerLinkActive="active-nav-item" [routerLinkActiveOptions]="{ exact: false }" class="admin-menu-list"
            (click)="goToAction()">
            <mat-icon matListItemIcon>alarm</mat-icon>
            @if (menuOpened) {
            <span>{{ 'ADMIN.IMPORTS' | translate }}</span>
            }
          </mat-list-item>
          }
          @if (authService.hasRole(RolesEnum.IMPORT_WRITE) &&
          organizationService.currentOrganization.plugins.includes(PluginEnum.GIM)) {
          <mat-list-item [routerLink]="[organizationService.currentOrganization.oid, 'gim']"
            routerLinkActive="active-nav-item" [routerLinkActiveOptions]="{ exact: false }" class="admin-menu-list"
            (click)="goToAction()">
            <mat-icon matListItemIcon>sync_alt</mat-icon>
            @if (menuOpened) {
            <span>{{ 'gim.title' | translate }}</span>
            }
          </mat-list-item>
          }
        </mat-nav-list>
      </div>
      }
      }
      <div class="version-footer">
        <div class="version-badge">
          <span class="version-label">{{ 'version' | translate }} :</span>
          <span class="version-number">{{ version }}</span>
        </div>
      </div>
    </mat-sidenav>
    }
    <mat-sidenav-content [ngClass]="{
    'admin-app-desktop-small' : menuDisplayed() && !menuOpened && !smallLayout,
    'px-4 lg:px-8 py-4 h-full': authService.isLoggedIn}">
      @if (dataService.dataLoading) {
      <div class="app-spinner">
        <mat-spinner mode="indeterminate" diameter="70"></mat-spinner>
      </div>
      }
      <div class="h-full w-full">
        <router-outlet></router-outlet>
      </div>
    </mat-sidenav-content>
  </mat-sidenav-container>
</main>